import { useEffect, useState } from "react";
import axios from "axios";
import { CreditCard, Loader2, MousePointerClick, ShoppingBag, UserPlus } from "lucide-react";
import { money, cardCls } from "@/lib/creators";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { click: MousePointerClick, sale: ShoppingBag, application: UserPlus, payout: CreditCard };
const LABEL = { click: "Click", sale: "Sale", application: "Applied", payout: "Payout" };
const sumMoney = (m) => Object.entries(m || {}).map(([c, v]) => money(v, c)).join(" + ") || money(0);
const ago = (iso) => {
  const m = Math.round((Date.now() - new Date(iso)) / 60000);
  return m < 60 ? `${Math.max(m, 1)}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

function Totals({ label, w, id }) {
  const cells = [["Clicks", w.clicks], ["Sales", w.sales], ["Revenue", sumMoney(w.revenue)], ["Commission", sumMoney(w.commission)], ["Applications", w.applications]];
  return (
    <div className={cardCls} data-testid={`activity-totals-${id}`}>
      <p className="text-[11px] uppercase tracking-[0.25em] gold-text mb-3">{label}</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {cells.map(([k, v]) => <div key={k}><p className="text-xs text-[#A7B0C0]">{k}</p><p data-testid={`activity-${id}-${k.toLowerCase()}`} className="text-xl font-light text-starlight">{v}</p></div>)}
      </div>
    </div>
  );
}

export default function CreatorActivity({ token, onExpired }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => axios.get(`${API}/admin/creators/overview`, { headers: { "X-Analytics-Token": token } }).then(({ data }) => setD(data))
      .catch((e) => e.response?.status === 401 && onExpired());
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [token, onExpired]);
  if (!d) return <Loader2 className="animate-spin gold-text" />;
  return (
    <div className="space-y-5" data-testid="creator-activity">
      <p className="text-sm text-[#A7B0C0]" data-testid="activity-counts">
        {d.counts.approved} approved · {d.counts.pending} pending · {d.counts.rejected} rejected · {d.counts.suspended} suspended
      </p>
      <div className="grid gap-4 lg:grid-cols-2">
        <Totals label="Last 7 days" w={d.d7} id="7d" />
        <Totals label="Last 30 days" w={d.d30} id="30d" />
      </div>
      <div className="grid gap-5 lg:grid-cols-5">
        <section className={`${cardCls} lg:col-span-3`} data-testid="activity-feed">
          <h2 className="text-xl mb-3">Live feed <span className="text-xs text-[#A7B0C0]">· refreshes every minute</span></h2>
          {d.feed.length === 0 ? <p className="text-sm text-[#A7B0C0]">No creator activity yet.</p> : (
            <ul className="divide-y divide-[#D4AF37]/10 max-h-[560px] overflow-y-auto pr-1">
              {d.feed.map((e, i) => {
                const Icon = ICONS[e.type];
                return (
                  <li key={i} className="flex items-start gap-3 py-2.5 text-sm" data-testid={`feed-item-${i}`}>
                    <Icon size={15} className={`shrink-0 mt-0.5 ${e.type === "sale" || e.type === "payout" ? "gold-text" : "text-[#A7B0C0]"}`} />
                    <span className="flex-1 min-w-0"><span className="text-starlight">{LABEL[e.type]}</span> · {e.name} <span className="gold-text">@{e.handle}</span>
                      <span className="block text-xs text-[#A7B0C0] truncate">{e.detail}{e.status ? ` · ${e.status}` : ""}</span></span>
                    <span className="text-xs text-[#A7B0C0] shrink-0">{ago(e.at)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
        <section className={`${cardCls} lg:col-span-2`} data-testid="activity-top">
          <h2 className="text-xl mb-3">Top creators · 30 days</h2>
          {d.top.length === 0 ? <p className="text-sm text-[#A7B0C0]">No clicks or sales in the last 30 days.</p> : (
            <ol className="space-y-2">
              {d.top.map((t, i) => (
                <li key={t.creatorId} className="flex items-center gap-3 text-sm" data-testid={`top-creator-${t.handle}`}>
                  <span className="w-6 h-6 rounded-full bg-[#D4AF37]/15 gold-text flex items-center justify-center text-xs">{i + 1}</span>
                  <span className="flex-1 min-w-0 truncate">{t.name} <span className="gold-text">@{t.handle}</span></span>
                  <span className="text-xs text-[#A7B0C0] text-right">{t.clicks} clicks · {t.sales} sales<br /><span className="gold-text">{money(t.commission)}</span></span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}
